/**
 * Lo que NO puede cruzar el cable hacia la API de la IA (requisito §10,
 * docs/AI_ARCHITECTURE.md §10, docs/SECURITY_PRIVACY.md §5).
 *
 * Vive en `contracts` y no en `domain` a proposito. No es una regla de
 * negocio: es parte del contrato de lo que sale del dispositivo, y tiene que
 * correr en los DOS extremos —el dominio, antes de construir la peticion, y la
 * Edge Function, que es el punto de salida real hacia internet—. `contracts`
 * es el unico paquete que se copia a `supabase/functions/_shared`
 * (`scripts/sync-contracts.mjs`), asi que ponerlo aqui es lo que hace que los
 * dos lados compartan UNA definicion en vez de dos que se separan con el
 * tiempo.
 *
 * Se aplica sobre el JSON ya serializado, asi que da igual como este anidado.
 */

/**
 * Claves que nunca pueden aparecer, este donde este el campo.
 *
 * `id` incluido: los identificadores del sistema son UUID y no le dicen nada
 * util al modelo. Lo que la IA necesita para razonar son numeros y codigos de
 * motivo, no a quien pertenecen.
 */
const CLAVES_PROHIBIDAS =
  /"(name|displayName|display_name|email|userId|user_id|id|deviceId|device_id|originDeviceId|token|phone|address)"\s*:/i;

/** Patrones de VALOR que no pueden viajar aunque la clave sea inocente. */
const VALORES_PROHIBIDOS = [
  // Correo electronico.
  /[\w.+-]+@[\w-]+\.[\w.]+/,
  // UUID: todos los identificadores del sistema lo son.
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i,
];

export interface ContextAudit {
  clean: boolean;
  violations: string[];
}

/**
 * Audita un contexto antes de enviarlo.
 *
 * Devuelve las infracciones en vez de lanzar, para que quien llama decida: en
 * produccion se cancela la llamada y se usa el camino determinista; en los
 * tests se afirma que la lista esta vacia (CA-7.7).
 */
export function auditAIContext(context: unknown): ContextAudit {
  const json = JSON.stringify(context) ?? '';
  const violations: string[] = [];

  const clave = CLAVES_PROHIBIDAS.exec(json);
  if (clave !== null) violations.push(`clave identificativa: ${clave[1]}`);

  for (const patron of VALORES_PROHIBIDOS) {
    if (patron.test(json)) violations.push(`valor con forma identificativa: ${patron.source}`);
  }

  return { clean: violations.length === 0, violations };
}

/**
 * Forma canonica de un contexto, para poder identificarlo por su hash.
 *
 * Ordena las claves en TODOS los niveles. Sin eso, dos contextos identicos
 * construidos en distinto orden darian hashes distintos y la cache del §7.2
 * no acertaria nunca —sin fallar, sin avisar: solo pagando cada peticion dos
 * veces—.
 *
 * Los arrays conservan su orden: en `weeklySeries` el orden ES el dato.
 */
export function canonicalJson(valor: unknown): string {
  if (valor === null || typeof valor !== 'object') return JSON.stringify(valor) ?? 'null';
  if (Array.isArray(valor)) return `[${valor.map(canonicalJson).join(',')}]`;

  const entradas = Object.entries(valor as Record<string, unknown>)
    // `undefined` no sobrevive a `JSON.stringify`, asi que tampoco puede
    // participar en el hash: si no, el mismo contexto daria dos claves segun
    // si el campo ausente se escribio como ausente o como `undefined`.
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([clave, v]) => `${JSON.stringify(clave)}:${canonicalJson(v)}`);

  return `{${entradas.join(',')}}`;
}

/**
 * Web Crypto y TextEncoder, declarados a mano.
 *
 * `tsconfig.base.json` fija `lib: ["ES2022"]` sin DOM, y es a proposito:
 * `contracts` lo comparten el navegador, Node y Deno, y con la libreria del
 * DOM disponible acabaria colandose un `window` o un `document` en un paquete
 * que no puede tenerlos. Declarar los dos globales que se usan de verdad
 * cuesta seis lineas y no abre esa puerta. Al ser declaraciones dentro de un
 * modulo no se exportan a nadie, y en ejecucion resuelven al global real.
 */
declare const crypto: {
  subtle: { digest(algoritmo: string, datos: Uint8Array): Promise<ArrayBuffer> };
};
declare class TextEncoder {
  encode(texto: string): Uint8Array;
}

/**
 * SHA-256 hexadecimal de la forma canonica. La misma funcion en el cliente y
 * en la Edge Function, que recalcula y compara.
 *
 * Usa Web Crypto, disponible tal cual en el navegador y en Deno.
 */
export async function contextHash(contexto: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalJson(contexto));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
